import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Select from 'react-select';
import CabecalhoAdmin from "../../../componentes/CabecalhoAdmin";
import MenuLateral from "../../../componentes/MenuLateral";
import ErroSpan from "../../../componentes/ErroSpan";
import Botao from "../../../componentes/Botao";
import comissaoSchema from "../../../schemas/comissaoSchema";
import Titulo1 from "../../../componentes/Titulo1";
import { AreaSuperior, ColunaTabela, LinhaTabela, OpcoesFiltragem, SecaoAdmin, SecaoPrincipal, Tabela } from "../AdminStyled";
import { listarGuias } from "../../../servicos/guiasServices";
import { salvarComissoes } from "../../../servicos/comissoesServices";

export default function CriarComis(){
    const navigate = useNavigate();
    const [guias, setGuias] = useState([]);
    const [estaResetando, setEstaResetando] = useState(false);
    const [status, setStatus] = useState({
        erro: false,
        sucesso: false,
        mensagem: undefined
    });
    
    const { 
        control,
        watch,
        register: registerComissao, 
        handleSubmit: handleSubmitComissao, 
        reset: resetComissao,
        formState: { errors: errosComissao }
    } = useForm({
        resolver: zodResolver(comissaoSchema)
    });

    const comissaoTotal = Number(watch("comissaoTour")) + Number(watch("comissaoLojaTour")) + Number(watch("comissaoLojaCentro"));

    useEffect(() => {
        obterGuias();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effect para atualizar o status toda vez que o formulário é alterado
    useEffect(() => {
        // Se for durante o reset não atualiza o status
        if (!estaResetando) {
            setStatus({ erro: false, sucesso: false, mensagem: "" });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comissaoTotal]);

    async function obterGuias(){
        listarGuias().then( resposta => {
            // Verifica se o retorno da consulta de guias retornou status 401
            if (resposta.status === 401) return navigate("/login", { state: { redirecionar: window.location.pathname }});

            // Cria vetor para armazenar guias
            let listagemGuias = [];

            // Adiciona id e nome dos guias ao vetor
            resposta.map(guia => {
                return listagemGuias.push({
                    value: guia._id,
                    label: guia.nome.toString()
                })
            });

            // Aplica o vetor de guias
            setGuias(listagemGuias);
        });
    }

    async function SalvarNovaComissao(dados) {
        setStatus({
            erro: true,
            sucesso: false,
            mensagem: "Carregando"
        });

        // Função para adicionar zeros a esquerda
        const zeroPad = (num, places) => String(num).padStart(places, '0');

        // Data atual para salvar como identificador da comissão
        const data = new Date(Date.now());
        const dataHora = zeroPad(data.getDate(), 2) + zeroPad(data.getMonth() + 1, 2) + data.getFullYear() + zeroPad(data.getHours(), 2) + zeroPad(data.getMinutes(), 2) + zeroPad(data.getSeconds(), 2);

        // Acrescenta identificador, comissão total e mensagem e manipula os outros valores para ficarem de acordo
        dados = {
            comissaoTour: Number(dados.comissaoTour),
            comissaoLojaTour: Number(dados.comissaoLojaTour),
            comissaoLojaCentro: Number(dados.comissaoLojaCentro),
            dataInicial: dados.dataInicial.toISOString().split("T")[0],
            dataFinal: dados.dataFinal.toISOString().split("T")[0],
            dataPagamento: dados.dataPagamento.toISOString().split("T")[0],
            guia: dados.guia.value,
            comissaoTotal: comissaoTotal,
            identificador: "MAN" + dataHora,
            mensagem: "Comissão manual",
            pagamentoEfetivado: dados.pagamentoEfetivado.value === "true" ? true : false
        };

        salvarComissoes([dados]).then( () => {
            // Altera flag estaResetando para não atualizar status ao resetar formulario
            setEstaResetando(true);

            // Reseta o formulário
            resetComissao();

            // Espera um ciclo para não atualizar status durante reset
            setTimeout(() => setEstaResetando(false), 2000);

            // Atualiza status para sucesso de criação de comissão
            setStatus({
                erro: false,
                sucesso: true,
                mensagem: "Comissão salva."
            });
        });
    }

    return (<SecaoAdmin>
        <MenuLateral />
        <SecaoPrincipal>
            {CabecalhoAdmin("Cadastro manual de comissões")}
            {status.erro && <ErroSpan>{"Erro ao criar comissão. " + status.mensagem}</ErroSpan>}
            {status.sucesso && <ErroSpan $corFundo={"#ddffcd"} $corTexto={"#209e00"} >{status.mensagem}</ErroSpan>}
            <form onSubmit={handleSubmitComissao(SalvarNovaComissao)} style={{ marginBottom: "20px" }}>
                <AreaSuperior>
                    <Titulo1 $margem="0 0 0 0" $tamanho="20px">Comissão total: {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL"
                    }).format(comissaoTotal || 0)}</Titulo1>
                    <OpcoesFiltragem style={{ marginTop: "10px" }}>
                        <Botao type="submit" $corFundo="#3a170ef8" $cor="white" $marginRight="10px">Salvar</Botao>
                        <Link to="/admin/comissoes/manual"><Botao>Cancelar</Botao></Link> 
                    </OpcoesFiltragem>
                </AreaSuperior>
                <Tabela>
                    <tbody>
                        <LinhaTabela>
                            <ColunaTabela $cor="#252525" $tamanho="200px">Guia</ColunaTabela>
                            <ColunaTabela>
                                <Controller
                                    name={"guia"}
                                    control={control}
                                    defaultValue={ null }
                                    render={({ field }) => (
                                        <Select
                                            isClearable={true}
                                            styles={{
                                                control: (baseStyles, state) => ({
                                                    ...baseStyles,
                                                    padding: 0,
                                                    margin: 0,
                                                    width: "100%",
                                                    fontSize: "14px",
                                                    color: "#252525",
                                                    borderColor: state.hover ? 'black' : 'rgba(58, 23, 14, 0.308)' 
                                                })
                                            }}
                                            classNamePrefix={'Select'}
                                            placeholder={"Selecione o guia"}
                                            {...field}
                                            options={guias}
                                        />
                                    )}
                                />
                            </ColunaTabela>
                            <ColunaTabela>
                                {errosComissao.guia && <ErroSpan>{errosComissao.guia.message}</ErroSpan>}
                            </ColunaTabela>
                        </LinhaTabela>
                        <LinhaTabela>
                            <ColunaTabela $cor="#252525" $tamanho="200px">Data inicial</ColunaTabela>
                            <ColunaTabela>
                                <input type="date" defaultValue={new Date().toISOString().split("T")[0]} {...registerComissao("dataInicial", {required: "Campo obrigatório"})}/>
                            </ColunaTabela>
                            <ColunaTabela $tamanho="400px">
                                {errosComissao.dataInicial && <ErroSpan>{errosComissao.dataInicial.message}</ErroSpan>}
                            </ColunaTabela>
                        </LinhaTabela>
                        <LinhaTabela>
                            <ColunaTabela $cor="#252525" $tamanho="200px">Data final</ColunaTabela>
                            <ColunaTabela>
                                <input type="date" defaultValue={new Date().toISOString().split("T")[0]} {...registerComissao("dataFinal", {required: "Campo obrigatório"})}/>
                            </ColunaTabela>
                            <ColunaTabela $tamanho="400px">
                                {errosComissao.dataFinal && <ErroSpan>{errosComissao.dataFinal.message}</ErroSpan>}
                            </ColunaTabela>
                        </LinhaTabela>
                        <LinhaTabela>
                            <ColunaTabela $cor="#252525" $tamanho="200px">Data pagamento</ColunaTabela>
                            <ColunaTabela>
                                <input type="date" defaultValue={new Date().toISOString().split("T")[0]} {...registerComissao("dataPagamento", {required: "Campo obrigatório"})}/>
                            </ColunaTabela>
                            <ColunaTabela $tamanho="400px">
                                {errosComissao.dataPagamento && <ErroSpan>{errosComissao.dataPagamento.message}</ErroSpan>}
                            </ColunaTabela>
                        </LinhaTabela>
                        <LinhaTabela>
                            <ColunaTabela $cor="#252525" $tamanho="200px">Comissão Tour</ColunaTabela>
                            <ColunaTabela>
                                <input type="number" step="any" defaultValue={0} {...registerComissao("comissaoTour", {required: "Campo obrigatório"})}/>
                            </ColunaTabela>
                            <ColunaTabela $tamanho="400px">
                                {errosComissao.comissaoTour && <ErroSpan>{errosComissao.comissaoTour.message}</ErroSpan>}
                            </ColunaTabela>
                        </LinhaTabela>
                        <LinhaTabela>
                            <ColunaTabela $cor="#252525" $tamanho="200px">Comissão loja Tour</ColunaTabela>
                            <ColunaTabela>
                                <input type="number" step="any" defaultValue={0} {...registerComissao("comissaoLojaTour", {required: "Campo obrigatório"})}/>
                            </ColunaTabela>
                            <ColunaTabela $tamanho="400px">
                                {errosComissao.comissaoLojaTour && <ErroSpan>{errosComissao.comissaoLojaTour.message}</ErroSpan>}
                            </ColunaTabela>
                        </LinhaTabela>
                        <LinhaTabela>
                            <ColunaTabela $cor="#252525" $tamanho="200px">Comissão loja centro</ColunaTabela>
                            <ColunaTabela>
                                <input type="number" step="any" defaultValue={0} {...registerComissao("comissaoLojaCentro", {required: "Campo obrigatório"})}/>
                            </ColunaTabela>
                            <ColunaTabela $tamanho="400px">
                                {errosComissao.comissaoLojaCentro && <ErroSpan>{errosComissao.comissaoLojaCentro.message}</ErroSpan>}
                            </ColunaTabela>
                        </LinhaTabela>
                        <LinhaTabela>
                            <ColunaTabela $cor="#252525" $tamanho="200px">Pagamento efetivado</ColunaTabela>
                            <ColunaTabela>
                                <Controller
                                    name={"pagamentoEfetivado"}
                                    control={control}
                                    defaultValue={ null }
                                    render={({ field }) => (
                                        <Select
                                            isClearable={true}
                                            styles={{
                                                control: (baseStyles, state) => ({
                                                    ...baseStyles,
                                                    padding: 0,
                                                    margin: 0,
                                                    width: "100%",
                                                    fontSize: "14px",
                                                    color: "#252525",
                                                    borderColor: state.hover ? 'black' : 'rgba(58, 23, 14, 0.308)' 
                                                })
                                            }}
                                            classNamePrefix={'Select'}
                                            placeholder={"Foi pago?"}
                                            {...field}
                                            options={[
                                                { value: "true", label: "Sim" },
                                                { value: "false", label: "Não"}
                                            ]}
                                        />
                                    )}
                                />
                            </ColunaTabela>
                            <ColunaTabela>
                                {errosComissao.pagamentoEfetivado && <ErroSpan>{errosComissao.pagamentoEfetivado.message}</ErroSpan>}
                            </ColunaTabela>
                        </LinhaTabela>
                    </tbody>
                </Tabela>
            </form>
        </SecaoPrincipal>
    </SecaoAdmin>)
}